import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import styled, { css } from 'styled-components';
import moment from '@g4/moment-utc';
import hooks, { SELECT_DATE } from '@g4/automation-hooks';

import Text from '@g4/boneyard/lib/Text';
import oldTheme from '../../Colors';

export const DAY_TYPE_EMPTY = 'DAY/TYPE/EMPTY'; // blank
export const DAY_TYPE_AVAILABLE = 'DAY/TYPE/AVAILABLE'; // available
export const DAY_TYPE_ACTIVE = 'DAY/TYPE/ACIVE'; // light blue or dark blue (if highlighted)

// const DAY_TYPE_HIGHLIGHTED = 'DAY/TYPE/HIGHLIGHTED'; // dark blue

export const DAY_SHAPE_DEFAULT = 'DAY/SHAPE/DEFAULT'; // [1st]
export const DAY_SHAPE_SINGLE = 'DAY/SHAPE/SINGLE'; // [(3rd)]
export const DAY_SHAPE_LEADING = 'DAY/SHAPE/LEADING'; // [(2nd]
export const DAY_SHAPE_TRAILING = 'DAY/SHAPE/TRAILING'; // [5th)]

export const DAY_POSITION_START = 'DAY/POSITION/START'; // start of week
export const DAY_POSITION_DEFAULT = 'DAY/POSITION/DEFAULT'; // somewhere between
export const DAY_POSITION_END = 'DAY/POSITION/END'; // end of week

export const DayNumber = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;

  ${props =>
    props.type === DAY_TYPE_ACTIVE &&
    css`
      color: #000;
    `} ${props =>
  props.disabled &&
  css`
    color: ${oldTheme.colors.modern.datePicker.disabled};
  `} ${props =>
  props.highlighted &&
  css`
    color: #fff;
    background-color: ${oldTheme.colors.modern.datePicker.active};
  `} ${props =>
  props.isCurrentDay &&
  css`
    border: 2px solid ${oldTheme.colors.common.secondary};
    border-radius: 50%;
  `};
`;

export const InteractiveDay = styled.button`
  color: inherit;
  font-weight: bold;
`;

export class Day extends PureComponent {
  static propTypes = {
    /** Determines if day is to be highlighted to the user */
    highlighted: PropTypes.bool,
    /** Determines if day is the current day */
    today: PropTypes.bool,
    /** Determines if day is disabled */
    disabled: PropTypes.bool,
    /** Type of day */
    type: PropTypes.oneOf([
      DAY_TYPE_EMPTY,
      DAY_TYPE_ACTIVE,
      DAY_TYPE_AVAILABLE,
    ]),
    /**
      Determines how the day should be styled, in relation to the selections
      made by the `DateRangePicker`
    */
    shape: PropTypes.oneOf([
      DAY_SHAPE_DEFAULT,
      DAY_SHAPE_SINGLE,
      DAY_SHAPE_LEADING,
      DAY_SHAPE_TRAILING,
    ]),
    /** Determines position of day, relative to its week */
    position: PropTypes.oneOf([
      DAY_POSITION_START,
      DAY_POSITION_END,
      DAY_POSITION_DEFAULT,
    ]),
    /** The date represented by this component */
    date: PropTypes.object,
    onClick: PropTypes.func,
    /** injects the generated stylesheet */
    className: PropTypes.string,
  };

  static defaultProps = {
    highlighted: false,
    today: false,
    type: DAY_TYPE_AVAILABLE,
    shape: DAY_SHAPE_DEFAULT,
    position: DAY_POSITION_DEFAULT,
    onClick: Function.prototype,
    date: moment(),
  };

  constructor(props) {
    super(props);

    this.handleClick = this.handleClick.bind(this);
  }

  handleClick() {
    this.props.onClick(this.props.date);
  }

  render() {
    const { type, date, disabled, highlighted, today } = this.props;

    if (type === DAY_TYPE_EMPTY) {
      return (
        <li className={this.props.className} aria-hidden="true">
          {date.format('D')}
        </li>
      );
    }

    if (disabled) {
      return (
        <li className={this.props.className} aria-hidden="true">
          <DayNumber
            highlighted={highlighted}
            disabled={disabled}
            type={type}
            isCurrentDay={today}
          >
            {date.format('D')}
          </DayNumber>
        </li>
      );
    }

    return (
      <li
        data-datestamp={date.format('YYYY-MM-DD')}
        aria-label={`${date.format('dddd')} the ${date.format('do')}`}
        className={this.props.className}
        {...hooks.hook(SELECT_DATE)}
        onClick={this.handleClick}
      >
        <DayNumber
          highlighted={highlighted}
          disabled={disabled}
          type={type}
          isCurrentDay={today}
        >
          <InteractiveDay
            type="button"
            {...hooks.hook(hooks.withValue(SELECT_DATE, date.format('D')))}
          >
            <Text size={16}>{date.format('D')}</Text>
          </InteractiveDay>
        </DayNumber>
      </li>
    );
  }
}

const StyledDay = styled(Day)`
  position: relative;
  float: left;
  width: calc(100% / 7);
  padding-bottom: calc(100% / 7);
  display: inline-block;
  text-align: center;

  ${props => {
    switch (props.type) {
      case DAY_TYPE_EMPTY: {
        return css`
          visibility: hidden;
          padding-bottom: 0;
        `;
      }

      case DAY_TYPE_ACTIVE: {
        return css`
          background-color: ${oldTheme.colors.modern.datePicker.highlight};
        `;
      }

      default:
        break;
    }
  }} ${props => {
  switch (props.shape) {
    case DAY_SHAPE_SINGLE: {
      return css`
        border-radius: 50%;
      `;
    }

    case DAY_SHAPE_LEADING: {
      return css`
        background-color: ${oldTheme.colors.modern.datePicker.highlight};
        border-top-left-radius: 50%;
        border-bottom-left-radius: 50%;
      `;
    }

    case DAY_SHAPE_TRAILING: {
      return css`
        background-color: ${oldTheme.colors.modern.datePicker.highlight};
        border-top-right-radius: 50%;
        border-bottom-right-radius: 50%;
      `;
    }

    default:
      break;
  }
}} ${props => {
  switch (props.position) {
    case DAY_POSITION_START: {
      return css`
            position: relative;

            &::before {
              content: '';
              display: block;
              position: absolute;
              top: 0;
              left: calc(${props => props.theme.spacing(2)} * -1);
              width: ${props => props.theme.spacing(2)};
              height: 100%;
            }

            ${props =>
              props.type === DAY_TYPE_ACTIVE &&
              css`
                &::before {
                  background-color: ${oldTheme.colors.modern.datePicker
                    .highlight};
                }
              `} ${props =>
        props.shape === DAY_SHAPE_SINGLE &&
        css`
          &::before {
            background-color: transparent;
          }
        `} ${props =>
        props.type === DAY_TYPE_ACTIVE &&
        props.shape === DAY_SHAPE_LEADING &&
        css`
          &::before {
            background-color: transparent;
          }
        `};
          `;
    }

    case DAY_POSITION_END: {
      return css`
            &::before {
              content: '';
              display: block;
              position: absolute;
              top: 0;
              right: calc(${props => props.theme.spacing(2)} * -1);
              width: ${props => props.theme.spacing(2)};
              height: 100%;
            }

            ${props =>
              props.type === DAY_TYPE_ACTIVE &&
              css`
                &::before {
                  background-color: ${oldTheme.colors.modern.datePicker
                    .highlight};
                }
              `} ${props =>
        props.shape === DAY_SHAPE_SINGLE &&
        css`
          &::before {
            background-color: transparent;
          }
        `} ${props =>
        props.type === DAY_TYPE_ACTIVE &&
        props.shape === DAY_SHAPE_TRAILING &&
        css`
          &::before {
            background-color: transparent;
          }
        `};
          `;
    }

    default:
      break;
  }
}};
`;

export default StyledDay;
